import { AuroraMesh, Button, buttonClasses, Chip, Faq, GradientHairline, TrustCard } from '@qi/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Check, CreditCard, Lock, Minus, ShieldCheck, Sparkles } from 'lucide-react';
import { useState } from 'react';
import {
  PLANS,
  QI_SCAN_FEATURES,
  UNLOCK_BULLETS,
  useCheckout,
} from '../components/marketing/PurchaseOptions.tsx';

const FREE_BULLETS = [
  '3 resonance scans a day, free forever',
  'Baseline harmonic protocols',
  'Last 7 days of trend history',
  'No card required',
];

export const Route = createFileRoute('/_marketing/upgrade')({
  component: UpgradePage,
});

// Comparison rows — `true`/`false` render the check/minus glyph;
// strings render verbatim so we can say "3 / day" vs "Unlimited".
type Cell = boolean | string;
const COMPARISON: ReadonlyArray<{ label: string; free: Cell; unlock: Cell; premium: Cell }> = [
  { label: 'Daily resonance scans', free: '3 / day', unlock: '3 / day', premium: 'Unlimited' },
  {
    label: 'Full scores + frequencies',
    free: false,
    unlock: 'This reading',
    premium: 'Every scan',
  },
  { label: '7-day rebalancing window', free: false, unlock: true, premium: true },
  {
    label: 'Harmonic protocol library',
    free: 'Baseline',
    unlock: 'This reading',
    premium: 'Full library',
  },
  { label: 'Trend history', free: 'Last 7 days', unlock: 'Last 7 days', premium: 'All-time' },
  { label: 'Before / after session comparison', free: false, unlock: false, premium: true },
  { label: 'Session library + saved favourites', free: false, unlock: false, premium: true },
  { label: 'Local-first scans', free: true, unlock: true, premium: true },
  { label: 'Export your data', free: true, unlock: true, premium: true },
  { label: 'Priority support', free: 'Community', unlock: 'Community', premium: 'Priority email' },
];

const TRUST = [
  {
    icon: <CreditCard size={18} strokeWidth={1.6} />,
    title: 'No card to try',
    body: 'Free really is free. Scan today without entering any payment details.',
  },
  {
    icon: <ShieldCheck size={18} strokeWidth={1.6} />,
    title: 'Cancel any time',
    body: 'One click in Settings. We never dark-pattern. Your scan history stays yours.',
  },
  {
    icon: <Lock size={18} strokeWidth={1.6} />,
    title: 'Local-first by default',
    body: 'Frames and audio stay on your device. We never sell biometric data.',
  },
] as const;

const FAQS = [
  {
    q: 'Do I need a credit card to start?',
    a: "No. The free plan is unmetered for sign-up — three scans a day, baseline protocols, forever. You'll only see a card field if you choose to upgrade.",
  },
  {
    q: 'Can I cancel any time?',
    a: 'Yes. Cancel from Settings in one click and you keep access until the end of the period. Your scan history stays in your account — no lock-in.',
  },
  {
    q: 'Why a subscription instead of a one-time purchase?',
    a: 'The frequency library and harmonic protocols grow as research lands. A subscription keeps your protocols current and funds the audio engine, the privacy work, and the next bio-signal channels.',
  },
  {
    q: 'What happens to my data if I downgrade?',
    a: "Your account and scans stay intact. You'll see the most recent 7 days of trend history on free, and you can export the rest at any time.",
  },
  {
    q: 'Is Qi Scan a medical device?',
    a: 'No. Qi Scan supports wellness optimization — relaxation, focus, recovery, emotional coherence. It does not diagnose or replace medical professionals.',
  },
  {
    q: 'When will Premium be available?',
    a: "Premium is in private beta while we tune the harmonic protocol library. Join the early-access list on this page and we'll email you when checkout opens.",
  },
] as const;

function UpgradePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden px-6 pt-14 pb-12">
        <AuroraMesh intensity="standard" position="top" />
        <div className="relative mx-auto flex max-w-3xl flex-col items-center gap-5 text-center">
          <Chip tone="aurora">
            <span className="inline-flex items-center gap-1.5 text-aurora">
              <Sparkles size={12} strokeWidth={2} aria-hidden="true" />
              <span className="text-ink-1">Pricing · always free to start</span>
            </span>
          </Chip>
          <h1 className="text-balance font-display text-5xl leading-[1.05] text-ink-0 md:text-6xl">
            Free is enough to feel it.{' '}
            <span className="text-aurora">Premium is for tracking it.</span>
          </h1>
          <p className="max-w-2xl text-pretty text-lg text-ink-1">
            Three full scans a day stay free, forever — no card required. Unlock one reading for
            $4.99, or go unlimited with Premium for the full harmonic library and your complete
            trend history.
          </p>
        </div>
      </section>

      <GradientHairline className="mx-auto max-w-5xl" />

      {/* Plans */}
      <section
        aria-labelledby="plans-heading"
        className="mx-auto max-w-5xl space-y-6 px-6 pt-12 pb-20"
      >
        <h2 id="plans-heading" className="sr-only">
          Plans
        </h2>
        <PlanGrid />
      </section>

      {/* Feature comparison */}
      <section aria-labelledby="compare-heading" className="mx-auto max-w-5xl space-y-6 px-6 pb-20">
        <header className="text-center">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">Compare</p>
          <h2 id="compare-heading" className="mt-2 text-balance font-display text-4xl text-ink-0">
            Side by side.
          </h2>
        </header>
        <div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
          {/* Header row */}
          <div className="grid grid-cols-[2fr_1fr_1fr_1.2fr] items-center gap-2 border-b border-hairline bg-surface-2 px-4 py-4 md:gap-4 md:px-6">
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
              Feature
            </span>
            <span className="text-center font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
              Free
            </span>
            <span className="text-center font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
              $4.99
            </span>
            <span className="text-center font-mono text-xs uppercase tracking-[0.18em] text-aurora">
              Premium
            </span>
          </div>
          {COMPARISON.map((row, idx) => (
            <div
              key={row.label}
              className={`grid grid-cols-[2fr_1fr_1fr_1.2fr] items-center gap-2 px-4 py-4 text-sm md:gap-4 md:px-6 ${idx > 0 ? 'border-t border-hairline' : ''}`}
            >
              <span className="text-ink-0">{row.label}</span>
              <span className="text-center text-ink-1">
                <Cell value={row.free} />
              </span>
              <span className="text-center text-ink-1">
                <Cell value={row.unlock} />
              </span>
              <span className="text-center text-ink-0">
                <Cell value={row.premium} accent />
              </span>
            </div>
          ))}
        </div>
      </section>

      <GradientHairline className="mx-auto max-w-5xl" />

      {/* Trust strip */}
      <section aria-labelledby="trust-heading" className="mx-auto max-w-5xl space-y-6 px-6 py-20">
        <header className="text-center">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">
            Why people stay
          </p>
          <h2 id="trust-heading" className="mt-2 text-balance font-display text-4xl text-ink-0">
            A subscription that respects you.
          </h2>
        </header>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {TRUST.map((t) => (
            <TrustCard key={t.title} glass icon={t.icon} title={t.title} body={t.body} />
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section aria-labelledby="faq-heading" className="mx-auto max-w-3xl space-y-6 px-6 pb-20">
        <header className="text-center">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">
            Common questions
          </p>
          <h2 id="faq-heading" className="mt-2 text-balance font-display text-4xl text-ink-0">
            Anything else?
          </h2>
        </header>
        <Faq items={FAQS} />
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden px-6 pb-20">
        <AuroraMesh intensity="subtle" position="bottom" />
        <div className="relative mx-auto max-w-3xl text-center">
          <h2 className="text-balance font-display text-4xl text-ink-0 md:text-5xl">
            Sixty seconds. A free scan. No card.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-pretty text-ink-1">
            See what resonates today. Decide about Premium once you've felt the pattern shift.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/onboarding"
              className={`${buttonClasses({ variant: 'resonance', size: 'lg' })} group`}
            >
              <span>Run my free scan</span>
              <ArrowRight
                size={14}
                strokeWidth={1.8}
                aria-hidden="true"
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * The three-column desktop plan grid — Free · Unlock $4.99 · Premium. The
 * Unlock and Premium cards drive the same shared `useCheckout` flow as the
 * funnel paywall, so pricing and checkout behaviour never drift between
 * surfaces; Premium is the featured annual plan from `PLANS`.
 */
function PlanGrid() {
  const navigate = useNavigate();
  const { pending, unlockOnce, checkout } = useCheckout();
  const [showFree, setShowFree] = useState(false);
  const monthly = PLANS.find((p) => p.id === 'monthly');
  const annual = PLANS.find((p) => p.id === 'annual');
  if (!monthly || !annual) return null;

  return (
    <div className="space-y-6">
      {/* The three paid paths — one-time unlock, then the two subscriptions. */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:items-start">
        <PlanCard
          name="Unlock your full scan"
          price="$4.99"
          cadence="one-time"
          note="Your complete resonance read, forever"
          bullets={UNLOCK_BULLETS}
        >
          <Button
            variant="resonance"
            size="lg"
            className="w-full"
            disabled={pending}
            onClick={() => void unlockOnce()}
          >
            {pending ? 'Unlocking…' : 'Unlock for $4.99'}
          </Button>
        </PlanCard>

        <PlanCard
          name={monthly.name}
          price={monthly.price}
          cadence={monthly.cadence}
          note={monthly.note}
          bullets={QI_SCAN_FEATURES}
        >
          <Button
            variant="ghost"
            size="lg"
            className="w-full"
            disabled={pending}
            onClick={() => void checkout(monthly.id)}
          >
            {pending ? 'Unlocking…' : monthly.cta}
          </Button>
        </PlanCard>

        <PlanCard
          name={annual.name}
          price={annual.price}
          strike={annual.strike}
          cadence={annual.cadence}
          note={annual.note}
          badge={annual.badge}
          bullets={QI_SCAN_FEATURES}
          featured
        >
          <Button
            variant="resonance"
            size="lg"
            className="w-full"
            disabled={pending}
            onClick={() => void checkout(annual.id)}
          >
            {pending ? 'Unlocking…' : annual.cta}
          </Button>
        </PlanCard>
      </div>

      {/* The free plan is the quiet default — surfaced on demand, not pushed. */}
      {showFree ? (
        <div className="mx-auto max-w-sm">
          <PlanCard
            name="Free"
            price="$0"
            cadence="forever"
            note="No card required"
            bullets={FREE_BULLETS}
          >
            <Button
              variant="ghost"
              size="lg"
              className="w-full"
              onClick={() => void navigate({ to: '/onboarding' })}
            >
              Start a free scan
            </Button>
          </PlanCard>
        </div>
      ) : (
        <p className="text-center">
          <button
            type="button"
            onClick={() => setShowFree(true)}
            className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2 underline-offset-4 transition-colors hover:text-aurora hover:underline"
          >
            Just looking? See the free plan
          </button>
        </p>
      )}
    </div>
  );
}

/** A single plan column for the upgrade-page grid. */
function PlanCard({
  name,
  price,
  strike,
  cadence,
  note,
  badge,
  bullets,
  featured,
  children,
}: {
  name: string;
  price: string;
  strike?: string;
  cadence: string;
  note: string;
  badge?: string;
  bullets: readonly string[];
  featured?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="relative h-full rounded-lg p-px"
      style={
        featured
          ? {
              background:
                'linear-gradient(135deg, color-mix(in oklab, var(--violet) 65%, transparent), color-mix(in oklab, var(--aurora) 75%, transparent))',
              boxShadow: 'var(--glow-aurora)',
            }
          : { background: 'var(--hairline)' }
      }
    >
      <div className="flex h-full flex-col gap-4 rounded-[calc(var(--radius-lg)-1px)] bg-surface-1 p-6">
        {badge && (
          <span
            className="absolute -top-2.5 right-5 inline-flex items-center gap-1 rounded-pill border border-aurora/40 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-aurora"
            style={{ background: 'color-mix(in oklab, var(--aurora) 18%, var(--abyss))' }}
          >
            {badge}
          </span>
        )}
        <div className="space-y-1">
          <p className="font-display text-lg text-ink-0">{name}</p>
          <p className="font-mono text-ink-0">
            {strike && (
              <span className="mr-1.5 text-sm tabular-nums text-ink-3 line-through">{strike}</span>
            )}
            <span className="text-3xl tabular-nums">{price}</span>
            <span className="text-xs text-ink-3"> {cadence}</span>
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">{note}</p>
        </div>
        <ul className="flex-1 space-y-1.5 text-sm text-ink-1">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <Check
                size={15}
                strokeWidth={2.4}
                className="mt-0.5 shrink-0 text-jade"
                aria-hidden="true"
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        {children}
      </div>
    </div>
  );
}

/** Comparison-table cell — bool → ✓/–, string → verbatim. */
function Cell({ value, accent }: { value: Cell; accent?: boolean }) {
  if (value === true) {
    return (
      <span
        role="img"
        aria-label="Included"
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${accent ? 'text-aurora' : 'text-jade'}`}
      >
        <Check size={14} strokeWidth={2.2} aria-hidden="true" />
      </span>
    );
  }
  if (value === false) {
    return (
      <span
        role="img"
        aria-label="Not included"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-ink-3"
      >
        <Minus size={12} strokeWidth={2} aria-hidden="true" />
      </span>
    );
  }
  return (
    <span className={accent ? 'font-mono text-sm text-aurora' : 'font-mono text-sm text-ink-1'}>
      {value}
    </span>
  );
}
